import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import settingsService from '@api/v1/settingsService'

/**
 * Firm-wide (tenant) Report Center settings — CC-50286, spec 083.
 *
 * The firm-wide Dashboards switch is a CB SERVER SETTING read at runtime, not a
 * build flag: `VITE_DASHBOARDS_TAB` is inlined by Vite at build time and one
 * build serves every tenant, so it cannot vary per firm. Read through the
 * EXISTING `/settings/server` endpoint (same mechanism as useUsePosting /
 * useFirmName) — no second read mechanism.
 */
export const FIRM_DASHBOARDS_SETTING = 'ReportCenterDashboardsEnabled'

/** Default when absent OR unreadable — see spec 083 §"Failure direction". */
export const FIRM_DASHBOARDS_DEFAULT = true

/**
 * Is the Dashboards tab enabled for this FIRM?
 *
 * Shared react-query cache (one key) so the three Report Center surfaces
 * — Reports, Queue, Manage Schedules — cost ONE fetch between them rather than
 * one each. cb-api additionally micro-caches the value per (tenant,key).
 *
 * Resolves TRUE while in flight or on a read failure, which is deliberately the
 * OPPOSITE of the per-user layer's default and is forced by the wire contract:
 * `/settings/server` returns '' both for "the firm never set this" and for "the
 * read threw" (cb-api catches per key). Failing closed on unreadable would
 * therefore also turn Dashboards off for every firm that never configured it.
 *
 * That is safe because this layer is ANDed with the per-user opt-in, which
 * defaults OFF — so an unreadable firm setting cannot reveal Dashboards to
 * anyone who has not personally opted in — and because CB enforces dashboard
 * DATA access server-side regardless (spec 038 §Permissions). This switch
 * decides whether the feature is OFFERED, not whether the user is AUTHORIZED.
 */
export const useFirmDashboardsEnabled = () => {
  const { data } = useQuery({
    queryKey: ['firm-settings', FIRM_DASHBOARDS_SETTING],
    queryFn: async () => {
      const s = await settingsService.getServerSettings([FIRM_DASHBOARDS_SETTING])
      return settingsService.toBool(s?.[FIRM_DASHBOARDS_SETTING], FIRM_DASHBOARDS_DEFAULT)
    },
    staleTime: 5 * 60_000,
    retry: false,
  })
  return data === undefined ? FIRM_DASHBOARDS_DEFAULT : data
}

/**
 * The admin view: the firm value PLUS whether this caller may change it.
 * Settings-page only — it costs cb-api a permission round-trip to CB.
 */
export const useFirmReportCenter = () =>
  useQuery({
    queryKey: ['firm-report-center'],
    queryFn: settingsService.getReportCenter,
    staleTime: 60_000,
    retry: false,
  })

/** Flip the firm-wide Dashboards switch. */
export const useSetFirmDashboards = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (enabled) => settingsService.setFirmDashboards(enabled),
    onSuccess: (saved) => {
      const enabled = saved?.dashboardsEnabled
      qc.setQueryData(['firm-report-center'], (prev) =>
        prev ? { ...prev, dashboardsEnabled: enabled } : prev
      )
      // Keep the GATE's cache honest, or the admin turns Dashboards off and the
      // tab stays up until the 5-minute staleTime lapses.
      qc.setQueryData(['firm-settings', FIRM_DASHBOARDS_SETTING], enabled)
    },
  })
}
